/**
 * 弹窗与确认框。
 *
 * 背景：建 Key、改策略、退款、调余额等操作都在弹窗里完成。
 * 设计意图：
 *   - createPortal 到 document.body，遮罩层级 9998、弹窗 9999；Esc 关闭。
 *   - 打开时 180ms 淡入；关闭时先撤掉 visible，150ms 后再卸载，退场动画完整播完。
 *   - 弹窗里有输入框时（dismissable=false）点遮罩不关闭，防止填了一半误触丢失；只能点关闭按钮或按 Esc。
 * 关键约束：busy 为 true 时（提交中）Esc 与关闭按钮都不生效，避免请求发出后界面消失、结果没人看到。
 */
import { IconX } from '@tabler/icons-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Button } from './primitives.tsx'

const EXIT_MS = 150

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissable = false,
  busy = false,
  wide = false,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  dismissable?: boolean
  busy?: boolean
  wide?: boolean
}) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const busyRef = useRef(busy)
  busyRef.current = busy

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const id = window.setTimeout(() => setMounted(false), EXIT_MS)
    return () => window.clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!mounted) return null
  return createPortal(
    <div
      className={`ap-overlay ${visible ? 'ap-overlay--in' : ''}`}
      onMouseDown={(e) => {
        if (dismissable && !busy && e.target === e.currentTarget) onClose()
      }}
    >
      <div className={`ap-modal ${wide ? 'ap-modal--wide' : ''}`} role="dialog" aria-modal="true">
        <div className="ap-modal__head">
          <div className="ap-modal__title">{title}</div>
          <Button variant="ghost" iconOnly aria-label={t(C.close)} onClick={onClose} disabled={busy} icon={<IconX size={18} />} />
        </div>
        <div className="ap-modal__body">{children}</div>
        {footer && <div className="ap-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

export function ConfirmModal({
  open,
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: ReactNode
  children: ReactNode
  confirmLabel?: ReactNode
  danger?: boolean
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      dismissable
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>
            {confirmLabel ?? t(C.confirm)}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  )
}
